import Carousel from '@rio-cloud/rio-uikit/Carousel';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { createDebugConfig, trackCustomEvent } from '@/configuration/setup/googleAnalytics';
import type { FindChargingStationsPageConfig } from '@/features/userManual/utils';
import { t } from '@/lib/utils';

const pagePrefix = 'userManual.findChargingStations';

const FindChargingStationsPageDialogContent = ({
    keyPage,
    images,
    subtitles,
    introText,
    counterText,
    footnotes,
    footnoteLink,
}: FindChargingStationsPageConfig) => {
    const [imageIndex, setImageIndex] = useState(0);
    const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

    const debugConfig = useMemo(() => createDebugConfig(), []);

    const trackImageEvent = useCallback(
        (index: number, page: string) => {
            trackCustomEvent(
                `show_image_${index + 1}`,
                {
                    trigger: 'click',
                    flow_name: `user_manual_find_charging_stations_${page}`,
                    element_name: `click_on_${page}_carousel`,
                },
                debugConfig
            );
        },
        [debugConfig]
    );

    useEffect(() => {
        setImageIndex(0);
        setLoadedImages(new Set());
        trackImageEvent(0, keyPage);
    }, [keyPage, trackImageEvent]);

    const handleImageSelect = useCallback(
        (index: number) => {
            setImageIndex(index);
            trackImageEvent(index, keyPage);
        },
        [keyPage, trackImageEvent]
    );

    const sanitizeHtml = useCallback((key: string) => DOMPurify.sanitize(t(key)), []);

    const sanitizeFootnote = useCallback(
        (key: string) => {
            const html = footnoteLink
                ? t(key).replace(
                      /<link>(.*?)<\/link>/g,
                      `<a href="${footnoteLink}" target="_blank" rel="noreferrer">$1</a>`
                  )
                : t(key);
            return DOMPurify.sanitize(html, { ADD_ATTR: ['target'] });
        },
        [footnoteLink]
    );

    const renderControlIcon = (icon: string) => (
        <span
            className={`rioglyph ${icon} display-flex justify-content-center align-items-center bg-lightest text-color-dark height-60 width-60`}
            style={{ borderRadius: '1000px' }}
        />
    );

    const counterItems = useMemo(() => counterText[imageIndex] ?? [], [counterText, imageIndex]);

    return (
        <>
            <h4 className='text-normal text-transform-none margin-top-20 margin-bottom-25'>
                <FormattedMessage id={`${pagePrefix}.title`} />
            </h4>

            <h5 className='text-normal margin-bottom-20'>
                <FormattedMessage id={`${pagePrefix}.${keyPage}.title`} />
            </h5>

            <Carousel
                key={keyPage}
                controls
                indicators
                touch
                interval={null}
                wrap={false}
                prevIcon={renderControlIcon('rioglyph-chevron-left')}
                nextIcon={renderControlIcon('rioglyph-chevron-right')}
                className='offset-indicators'
                onSelect={handleImageSelect}
            >
                {images.map((image, index) => (
                    <Carousel.Item key={index}>
                        {!loadedImages.has(index) && index === imageIndex && (
                            <div className='height-25vh'>
                                <Spinner />
                            </div>
                        )}
                        <img
                            className='height-auto width-100pct'
                            style={{
                                objectFit: 'contain',
                                maxHeight: '50vh',
                                display: !loadedImages.has(index) && index === imageIndex ? 'none' : 'block',
                            }}
                            src={`/findChargingStations${image}`}
                            alt={t(`${pagePrefix}.${keyPage}.image${index + 1}.imageAlt`)}
                            onLoad={() => setLoadedImages(prev => new Set(prev).add(index))}
                        />
                    </Carousel.Item>
                ))}
            </Carousel>

            {subtitles?.[imageIndex] && (
                <p className='text-bold'>{t(`${pagePrefix}.${keyPage}.${subtitles[imageIndex]}`)}</p>
            )}
            {introText?.[imageIndex] && (
                <p
                    // biome-ignore lint: sanitized content with DOMPurify
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(`${pagePrefix}.${keyPage}.${introText[imageIndex]}`),
                    }}
                />
            )}

            <div className='counter color-secondary filled size-small'>
                {counterItems.map((item, index) => {
                    // Calculate count number based on how many items before imageIndex
                    const previousCount = Object.values(counterText)
                        .slice(0, imageIndex)
                        .reduce((acc, arr) => acc + arr.length, 0);

                    const count = previousCount + index + 1;

                    return (
                        <div data-count={count} key={index} data-testId='bullet-counter'>
                            <span
                                // biome-ignore lint: sanitized content with DOMPurify
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(`${pagePrefix}.${keyPage}.${item}`),
                                }}
                            />
                        </div>
                    );
                })}
            </div>

            {footnotes?.[imageIndex] && (
                <p
                    className='text-italic margin-bottom-0 text-color-dark text-size-12'
                    // biome-ignore lint: sanitized content with DOMPurify
                    dangerouslySetInnerHTML={{
                        __html: sanitizeFootnote(`${pagePrefix}.${keyPage}.${footnotes[imageIndex]}`),
                    }}
                />
            )}
        </>
    );
};

export default FindChargingStationsPageDialogContent;
